import Button from 'components/Forms/Button/Button';
import { showInfoMessage } from 'components/Helpers/Toasts';
import { getIsProductAbleToRotate, rotateProduct } from 'helpers/configurator/configuratorRotationHelper';
import { getNewProductForDrawer } from 'helpers/configurator/getNewProductForDrawer';
import useTranslation from 'next-translate/useTranslation';
import { CloseIcon, RotateIcon } from 'public/svg/IconsSvg';
import { usePersistStore } from 'store/usePersistStore';
import { twJoin } from 'tailwind-merge';
import { ConfiguratorProductType } from 'types/configurator';

type ConfiguratorProductInDrawerActionsProps = {
    shouldShowButtons: boolean;
    product: ConfiguratorProductType;
};

export const ConfiguratorProductInDrawerActions: FC<ConfiguratorProductInDrawerActionsProps> = ({
    product,
    shouldShowButtons,
}) => {
    const { deleteConfiguratorProduct, drawerSizes, updateConfiguratorProduct, productsInDrawer } = usePersistStore();
    const { t } = useTranslation();

    const isAbleToRotate = getIsProductAbleToRotate(
        { ...product, height: product.width, width: product.height },
        productsInDrawer,
        drawerSizes,
    );

    const rotateProductHandler = () => {
        if (isAbleToRotate) {
            const rotatedProduct = rotateProduct(product);

            updateConfiguratorProduct(rotatedProduct, product.id);

            return;
        }

        const rotatedProduct = getNewProductForDrawer(productsInDrawer, drawerSizes, product, true);

        if (rotatedProduct) {
            updateConfiguratorProduct(rotatedProduct, product.id);

            return;
        }
        showInfoMessage(t('There is not enough space in the drawer for rotating this component.'));
    };

    return (
        <>
            {shouldShowButtons && (
                <Button
                    className="above aspect-ratio-fallback absolute right-0 top-0 aspect-square min-h-[32px] -translate-y-1/2 translate-x-1/2 p-0"
                    size="extraSmall"
                    type="button"
                    variant="secondary"
                    onClick={() => deleteConfiguratorProduct(product.id)}
                >
                    <CloseIcon className="!mr-0" />
                </Button>
            )}
            {shouldShowButtons && (
                <Button
                    size="extraSmall"
                    type="button"
                    variant="secondary"
                    className={twJoin(
                        'above aspect-ratio-fallback absolute left-0 top-0 aspect-square min-h-[32px] -translate-x-1/2 -translate-y-1/2 p-0',
                        !isAbleToRotate && 'active:scale-100',
                    )}
                    onClick={rotateProductHandler}
                >
                    <RotateIcon
                        className={twJoin(
                            '!mr-0 h-6 w-6 transition duration-500',
                            product.alignment === 'horizontal' ? 'rotate-0' : 'rotate-[359deg]',
                        )}
                    />
                </Button>
            )}
        </>
    );
};
